import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/base/PageHeader";
import { ListSkeleton } from "@/components/base/ListSkeleton";
import { EmptyState } from "@/components/base/EmptyState";
import { Modal } from "@/components/base/Modal";
import {
  listingTemplates,
  templateChannelLabel,
  type ListingTemplateMeta,
} from "@/lib/listing-templates";
import { providerRegistry } from "@/lib/integrations/registry";
import { useAccountTemplateAssignments } from "@/hooks/useTemplateAssignments";
import TemplateCard from "./components/TemplateCard";
import StoreDefaultRow from "./components/StoreDefaultRow";

export default function ListingTemplates() {
  const { org } = useAuth();
  const { assignments, assign } = useAccountTemplateAssignments(org?.id);

  const [assignTarget, setAssignTarget] = useState<ListingTemplateMeta | null>(null);
  const [assignError, setAssignError] = useState("");

  const { data: connectors = [], isLoading } = useQuery({
    queryKey: ["listing-template-connectors", org?.id],
    enabled: !!org?.id,
    queryFn: async () => {
      const { data } = await supabase
        .from("connectors")
        .select("id, name, provider, auth_status")
        .eq("org_id", org!.id)
        .order("created_at", { ascending: true });
      return (data ?? []) as { id: string; name: string; provider: string | null; auth_status: string | null }[];
    },
  });

  const providerIcon = (provider: string | null) =>
    providerRegistry.find((p) => p.provider === provider)?.icon ?? "ri-plug-line";
  const providerLabel = (provider: string | null) =>
    providerRegistry.find((p) => p.provider === provider)?.label ?? (provider ?? "Store");

  const grouped = providerRegistry
    .map((p) => ({ meta: p, items: connectors.filter((c) => c.provider === p.provider) }))
    .filter((g) => g.items.length > 0);

  const assignableFor = (template: ListingTemplateMeta) =>
    connectors.filter((c) => c.provider && (template.channels as string[]).includes(c.provider));

  return (
    <div>
      <PageHeader
        title="Listing Templates"
        subtitle="Choose how your listings will look on each store"
      >
        <Link
          to="/listing-templates/review"
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 whitespace-nowrap"
        >
          <i className="ri-layout-grid-line" /> Review &amp; compare
        </Link>
      </PageHeader>

      {/* Explainer */}
      <div className="mb-6 bg-gradient-to-r from-emerald-50 to-white border border-emerald-100 rounded-xl px-5 py-4">
        <div className="flex items-start gap-3">
          <span className="w-9 h-9 rounded-lg bg-emerald-600 flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-layout-masonry-line text-white" />
          </span>
          <div>
            <p className="text-sm font-semibold text-slate-800">
              Each connected store can use its own template.
            </p>
            <p className="text-sm text-slate-600 mt-0.5 leading-relaxed">
              Pick a template for every store you sell on, so two eBay shops or a BigCommerce store can each look
              different. The same product can appear differently on every store you run.
            </p>
          </div>
        </div>
      </div>

      {/* Template library */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
        {listingTemplates.map((template) => (
          <TemplateCard
            key={template.id}
            template={template}
            onUse={() => setAssignTarget(template)}
          />
        ))}
      </div>

      {/* Store defaults */}
      <div className="mt-12">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-slate-900">Store defaults</h2>
          <span className="text-xs text-slate-500">
            {connectors.length} connected store{connectors.length === 1 ? "" : "s"}
          </span>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {isLoading ? (
            <div className="p-6">
              <ListSkeleton />
            </div>
          ) : grouped.length === 0 ? (
            <EmptyState
              icon="ri-store-2-line"
              title="No stores connected yet"
              description="Connect an eBay or BigCommerce store first, then choose a default template for it here."
            />
          ) : (
            <div className="divide-y divide-slate-100">
              {grouped.map((group) => (
                <div key={group.meta.provider} className="p-4 sm:p-5">
                  <div className="flex items-center gap-2.5 mb-3">
                    <span className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                      <i className={`${group.meta.icon} text-slate-600`} />
                    </span>
                    <h3 className="text-sm font-semibold text-slate-800">{group.meta.label}</h3>
                    <span className="text-xs text-slate-400">
                      {group.items.length} store{group.items.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div className="space-y-3">
                    {group.items.map((connector) => (
                      <StoreDefaultRow
                        key={connector.id}
                        connectorId={connector.id}
                        name={connector.name}
                        provider={connector.provider}
                        providerLabel={providerLabel(connector.provider)}
                        providerIcon={providerIcon(connector.provider)}
                        savedId={assignments?.[connector.id] ?? null}
                        onSave={assign}
                      />
                    ))}
                  </div>
                </div>
              ))}

              {/* Existing listings note */}
              <div className="px-4 sm:px-5 py-4 bg-amber-50/60 border-t border-amber-100 flex items-start gap-3">
                <i className="ri-information-line text-amber-600 mt-0.5" />
                <p className="text-sm text-amber-800 leading-relaxed">
                  Changing a store&apos;s default only affects <strong>new and updated listings</strong>. Listings already
                  published keep their current look until you deliberately update and republish them.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Assign modal */}
      <Modal
        open={!!assignTarget}
        onClose={() => setAssignTarget(null)}
        title={`Use "${assignTarget?.name ?? ""}"`}
        footer={
          <button
            onClick={() => setAssignTarget(null)}
            className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap"
          >
            Close
          </button>
        }
      >
        {assignTarget && (
          <div>
            <p className="text-sm text-slate-600 leading-relaxed">
              Choose which store should use <strong>{assignTarget.name}</strong> as its default listing template. You can
              assign it to more than one store.
            </p>
            <p className="text-xs text-slate-400 mt-1 mb-4">
              Compatible with {templateChannelLabel(assignTarget.channels)}.
            </p>

            {assignError && <p className="mb-3 text-xs text-red-600">{assignError}</p>}

            {assignableFor(assignTarget).length === 0 ? (
              <EmptyState
                icon="ri-store-2-line"
                title="No compatible stores"
                description={`This template works with ${templateChannelLabel(assignTarget.channels)}. Connect a compatible store to assign it.`}
              />
            ) : (
              <div className="space-y-2">
                {assignableFor(assignTarget).map((connector) => {
                  const isAssigned = assignments?.[connector.id] === assignTarget.id;
                  return (
                    <div
                      key={connector.id}
                      className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 p-3"
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                          <i className={`${providerIcon(connector.provider)} text-slate-600`} />
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{connector.name}</p>
                          <p className="text-xs text-slate-400">{providerLabel(connector.provider)}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setAssignError("");
                          assign(connector.id, assignTarget.id).catch((err) =>
                            setAssignError((err as Error).message || "Could not assign this template."),
                          );
                        }}
                        disabled={isAssigned}
                        className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap ${
                          isAssigned
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-emerald-600 hover:bg-emerald-700 text-white"
                        }`}
                      >
                        {isAssigned ? "Assigned" : "Assign"}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}